/* @jsx h */
import { defineComponent, h, onMounted, ref } from "vue";
import { Loading, useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import type { CountSummary } from "./types";
import { CountEditor } from "./CountEditor";
import { SubpageHeader } from "@app/vue/components/SubpageHeader";

// Stock counts and what they adjusted. See
// docs/plans/inventory_and_costing/06_capture.md (#7).
export const InventoryCountsPage = defineComponent({
  name: "InventoryCountsPage",
  setup() {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();

    const loading = ref(true);
    const counts = ref<CountSummary[]>([]);
    const editing = ref<number | null | undefined>(undefined);

    const load = async () => {
      counts.value = (await trpc.stock.counts.query()) as CountSummary[];
    };

    onMounted(async () => {
      try {
        await load();
      } catch {
        toast.showToast(t("inventory.counts.load_failed"), { type: "error" });
      } finally {
        loading.value = false;
      }
    });

    const remove = async (count: CountSummary) => {
      if (!confirm(t("inventory.counts.delete_confirm"))) return;
      try {
        await trpc.stock.deleteCount.mutate({ id: count.id });
        await load();
      } catch {
        toast.showToast(t("inventory.counts.save_failed"), { type: "error" });
      }
    };

    return () => (
      <div class="flex h-full w-full flex-col overflow-hidden bg-base-200">
        <SubpageHeader
          title={t("inventory.nav.counts")}
          subtitle={t("inventory.counts.subtitle")}
        >
          <button
            class="btn btn-neutral btn-sm"
            onClick={() => (editing.value = null)}
          >
            <span class="material-symbols-outlined text-base">add</span>
            {t("inventory.counts.new")}
          </button>
        </SubpageHeader>

        <div class="flex-1 overflow-auto p-3">
          {loading.value ? (
            <div class="flex h-full items-center justify-center">
              <Loading />
            </div>
          ) : counts.value.length === 0 ? (
            <div class="flex h-full flex-col items-center justify-center gap-2 text-center">
              <span class="material-symbols-outlined text-5xl text-base-content/30">
                checklist
              </span>
              <p class="text-sm text-base-content/60">
                {t("inventory.counts.empty")}
              </p>
            </div>
          ) : (
            <div class="overflow-x-auto rounded-lg border border-base-300 bg-base-100">
              <table class="table table-sm">
                <thead>
                  <tr>
                    <th>{t("inventory.counts.date")}</th>
                    <th>{t("inventory.counts.name")}</th>
                    <th class="text-right">{t("inventory.counts.items")}</th>
                    <th class="text-right">
                      {t("inventory.counts.difference")}
                    </th>
                    <th>{t("inventory.purchases.col_status")}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {counts.value.map((c) => (
                    <tr
                      key={c.id}
                      class="cursor-pointer hover:bg-base-200/60"
                      onClick={() => (editing.value = c.id)}
                    >
                      <td>{new Date(c.counted_at).toLocaleDateString()}</td>
                      <td>
                        {c.name ?? <span class="text-base-content/40">—</span>}
                      </td>
                      <td class="text-right">{c.line_count}</td>
                      <td
                        class={[
                          "text-right font-medium",
                          c.difference_value < 0 ? "text-error" : "",
                        ].join(" ")}
                      >
                        {c.status === "posted"
                          ? c.difference_value.toFixed(2)
                          : "—"}
                      </td>
                      <td>
                        <span
                          class={
                            c.status === "posted"
                              ? "badge badge-success badge-sm"
                              : "badge badge-ghost badge-sm"
                          }
                        >
                          {t(`inventory.purchases.status_${c.status}`)}
                        </span>
                      </td>
                      <td class="text-right">
                        {c.status === "draft" && (
                          <button
                            class="btn btn-ghost btn-xs"
                            onClick={(e: MouseEvent) => {
                              e.stopPropagation();
                              remove(c);
                            }}
                          >
                            <span class="material-symbols-outlined text-base">
                              delete
                            </span>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {editing.value !== undefined && (
          <CountEditor
            countId={editing.value}
            onClose={async (changed: boolean) => {
              editing.value = undefined;
              if (changed) await load();
            }}
          />
        )}
      </div>
    );
  },
});
